import { Box } from "@mui/material";
import { useTheme } from "contexts/ThemeContext";

interface StatusChipProps {
  status: string;
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'outlined' | 'filled';
  customStyles?: {
    backgroundColor?: string;
    color?: string;
    borderColor?: string;
  };
}

export function StatusChip({ 
  status, 
  size = 'medium', 
  variant = 'default',
  customStyles 
}: StatusChipProps) {
  const { theme: customerTheme } = useTheme();
  const isAssettech = customerTheme.customer.id === 'assettech';

  // AssetTech PO status configurations with logical color schemes
  const assettechPOStatuses: Record<string, { label: string; bg: string; color: string }> = {
    // AssetTech Pickup States
    'wof_drafted': { label: 'WOF Drafted', bg: '#E0F2F1', color: '#00695C' },
    'pending_signature': { label: 'Pending Signature', bg: '#FFF3E0', color: '#F57C00' },
    'expired': { label: 'Expired', bg: '#FFEBEE', color: '#C62828' },
    'pending_scheduling': { label: 'Pending Scheduling', bg: '#E3F2FD', color: '#1565C0' },
    'pending_confirmation': { label: 'Pending Confirmation', bg: '#E0E0E0', color: '#616161' },
    'scheduled': { label: 'Scheduled', bg: '#B3E5FC', color: '#01579B' },
    'processing': { label: 'Processing', bg: '#FFE0B2', color: '#EF6C00' },
    'processed': { label: 'Processed', bg: '#C8E6C9', color: '#388E3C' },
    'Processed': { label: 'Processed', bg: '#C8E6C9', color: '#388E3C' },
    
    // Additional AssetTech PO statuses - Blue tones
    'pending': { label: 'Pending', bg: '#E3F2FD', color: '#1565C0' },
    'in_progress': { label: 'In Progress', bg: '#BBDEFB', color: '#1976D2' },
    'under_review': { label: 'Under Review', bg: '#64B5F6', color: '#1565C0' },
    
    // Success statuses - Green tones
    'complete': { label: 'Completed', bg: '#E8F5E8', color: '#2E7D32' },
    'completed': { label: 'Completed', bg: '#E8F5E8', color: '#2E7D32' },
    'finished': { label: 'Finished', bg: '#C8E6C9', color: '#388E3C' },
    'approved': { label: 'Approved', bg: '#A5D6A7', color: '#388E3C' },
    'successful': { label: 'Successful', bg: '#81C784', color: '#2E7D32' },
    
    // Warning statuses - Orange/Yellow tones
    'on_hold': { label: 'On Hold', bg: '#FFF3E0', color: '#F57C00' },
    'pending_review': { label: 'Pending Review', bg: '#FFE0B2', color: '#EF6C00' },
    'requires_attention': { label: 'Requires Attention', bg: '#FFCC02', color: '#E65100' },
    'delayed': { label: 'Delayed', bg: '#FFB74D', color: '#E65100' },
    
    // Error statuses - Red tones
    'cancelled': { label: 'Cancelled', bg: '#FFEBEE', color: '#C62828' },
    'canceled': { label: 'Canceled', bg: '#FFEBEE', color: '#C62828' },
    'failed': { label: 'Failed', bg: '#FFCDD2', color: '#D32F2F' },
    'rejected': { label: 'Rejected', bg: '#EF9A9A', color: '#C62828' },
    'error': { label: 'Error', bg: '#E57373', color: '#D32F2F' },
    
    // Neutral statuses - Gray tones
    'draft': { label: 'Draft', bg: '#F5F5F5', color: '#616161' },
    'unscheduled': { label: 'Unscheduled', bg: '#ECEFF1', color: '#455A64' },
    'archived': { label: 'Archived', bg: '#EEEEEE', color: '#757575' },
    'inactive': { label: 'Inactive', bg: '#E0E0E0', color: '#616161' },
  };

  // Standard status configurations
  const standardStatuses: Record<string, { label: string; bg: string; color: string }> = {
    missing_info: { label: "Missing Info", bg: "#FFCDD2", color: "#B71C1C" },
    pending_confirmation: { label: "Pending Confirmation", bg: "#E0E0E0", color: "#616161" },
    scheduled: { label: "Scheduled", bg: "#B3E5FC", color: "#01579B" },
    canceled: { label: "Canceled", bg: "#757575", color: "#FFFFFF" },
    processing: { label: "Processing", bg: "#FFE0B2", color: "#EF6C00" },
    on_hold: { label: "On Hold", bg: "#FFF176", color: "#F57F17" },
    complete: { label: "Complete", bg: "#C8E6C9", color: "#388E3C" },
    draft: { label: "Draft", bg: "#F5F5F5", color: "#616161" },
    unscheduled: { label: "Unscheduled", bg: "#ECEFF1", color: "#455A64" },
  };

  const getStatusConfig = (statusValue: string) => {
    if (!statusValue) {
      return { label: "Unknown", bg: "#E0E0E0", color: "#616161" };
    }

    // If custom styles are provided, use them
    if (customStyles) {
      return {
        label: statusValue,
        bg: customStyles.backgroundColor || "#E0E0E0",
        color: customStyles.color || "#616161"
      };
    }

    // For AssetTech, use PO status configurations
    if (isAssettech) {
      // Try exact match first
      if (assettechPOStatuses[statusValue]) {
        return assettechPOStatuses[statusValue];
      }
      
      // Try normalized match (lowercase with underscores)
      const normalizedStatus = statusValue.toLowerCase().replace(/[^a-z0-9_]/g, '_');
      return assettechPOStatuses[normalizedStatus] || {
        label: statusValue, // Use the original status value as label
        bg: "#E0E0E0",
        color: "#616161"
      };
    }

    // For other customers, use standard status configurations
    return standardStatuses[statusValue] || {
      label: statusValue, // Use the original status value as label
      bg: "#E0E0E0",
      color: "#616161"
    };
  };

  const statusConfig = getStatusConfig(status);

  // Size configurations
  const sizeConfig = {
    small: {
      padding: "1px 6px",
      fontSize: "0.625rem",
      borderRadius: "3px",
    },
    medium: {
      padding: "2px 8px",
      fontSize: "0.75rem",
      borderRadius: "4px",
    },
    large: {
      padding: "4px 12px",
      fontSize: "0.875rem",
      borderRadius: "6px",
    },
  };

  // Variant configurations
  const variantConfig = {
    default: {
      backgroundColor: statusConfig.bg,
      color: statusConfig.color,
      border: 'none',
    },
    outlined: {
      backgroundColor: 'transparent',
      color: statusConfig.color,
      border: `1px solid ${statusConfig.color}`,
    },
    filled: {
      backgroundColor: statusConfig.color,
      color: '#FFFFFF',
      border: 'none',
    },
  };

  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        fontWeight: "bold",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        ...sizeConfig[size],
        ...variantConfig[variant],
        ...(customStyles?.borderColor && variant === 'outlined' && {
          borderColor: customStyles.borderColor,
        }),
      }}
    >
      {statusConfig.label}
    </Box>
  );
}
